import { useMemo, useState } from 'react';
import { AlertCircle, Users, TrendingUp, TrendingDown, Zap, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { calculateStaffing } from '../../utils/erlangC';
import { ServiceLevelParams } from './ServiceLevelControls';

interface StaffingInterval {
  time: string;
  callVolume: number;
  required: number;
  scheduled: number;
  net: number;
  sla: number;
}

interface NetStaffingGridProps {
  forecastData: Array<{ time: string; volume: number }>;
  scheduledData: Array<{ time: string; agents: number }>;
  serviceLevelParams: ServiceLevelParams;
  onAutoFill?: (interval: string, gap: number) => void;
  intervalMinutes?: number;
}

export default function NetStaffingGrid({
  forecastData,
  scheduledData,
  serviceLevelParams,
  onAutoFill,
  intervalMinutes = 15,
}: NetStaffingGridProps) {
  const [hoveredInterval, setHoveredInterval] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);

  // Calculate staffing requirements for each interval
  const staffingIntervals = useMemo<StaffingInterval[]>(() => {
    return forecastData.map((forecast) => {
      // Find scheduled agents for this time
      const scheduled = scheduledData.find((s) => s.time === forecast.time)?.agents || 0;

      // Calculate required agents using Erlang C
      const staffing = calculateStaffing({
        callVolume: forecast.volume,
        ahtMinutes: serviceLevelParams.aht,
        targetSLPercent: serviceLevelParams.slaPercent,
        targetAnswerSeconds: serviceLevelParams.answerTime,
        shrinkagePercent: serviceLevelParams.shrinkage,
        intervalMinutes,
      });

      const required = staffing.requiredAgents;
      const net = scheduled - required;

      // Calculate expected SLA with current scheduled agents
      const expectedSLA = scheduled > 0
        ? calculateStaffing({
            callVolume: forecast.volume,
            ahtMinutes: serviceLevelParams.aht,
            targetSLPercent: serviceLevelParams.slaPercent,
            targetAnswerSeconds: serviceLevelParams.answerTime,
            shrinkagePercent: 0, // Don't apply shrinkage when checking SLA
            intervalMinutes,
          }).achievedServiceLevel
        : 0;

      return {
        time: forecast.time,
        callVolume: forecast.volume,
        required,
        scheduled,
        net,
        sla: expectedSLA,
      };
    });
  }, [forecastData, scheduledData, serviceLevelParams, intervalMinutes]);

  // Calculate summary stats
  const summary = useMemo(() => {
    const totalRequired = staffingIntervals.reduce((sum, int) => sum + int.required, 0);
    const totalScheduled = staffingIntervals.reduce((sum, int) => sum + int.scheduled, 0);
    const totalNet = totalScheduled - totalRequired;
    const avgSLA = staffingIntervals.reduce((sum, int) => sum + int.sla, 0) / staffingIntervals.length;

    const gapIntervals = staffingIntervals.filter((int) => int.net < 0);
    const overageIntervals = staffingIntervals.filter((int) => int.net > 0);

    return {
      totalRequired,
      totalScheduled,
      totalNet,
      avgSLA: Math.round(avgSLA * 10) / 10,
      gapCount: gapIntervals.length,
      overageCount: overageIntervals.length,
      maxGap: gapIntervals.length > 0 ? Math.min(...gapIntervals.map((i) => i.net)) : 0,
      maxOverage: overageIntervals.length > 0 ? Math.max(...overageIntervals.map((i) => i.net)) : 0,
    };
  }, [staffingIntervals]);

  // Get cell styling based on value
  const getCellStyle = (value: number, type: 'net' | 'sla') => {
    if (type === 'net') {
      if (value < 0) {
        // Understaffed - red
        return {
          bg: 'bg-red-50',
          border: 'border-red-300',
          text: 'text-red-900 font-bold',
          icon: <TrendingDown className="h-3 w-3 text-red-600" />,
        };
      } else if (value > 0) {
        // Overstaffed - green
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-800',
          icon: <TrendingUp className="h-3 w-3 text-green-600" />,
        };
      } else {
        // Perfect
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-800 font-medium',
          icon: null,
        };
      }
    } else {
      // SLA coloring
      if (value >= serviceLevelParams.slaPercent) {
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-800 font-semibold',
          icon: null,
        };
      } else {
        return {
          bg: 'bg-red-50',
          border: 'border-red-300',
          text: 'text-red-900 font-bold',
          icon: <AlertCircle className="h-3 w-3 text-red-600" />,
        };
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title={isExpanded ? 'Collapse table' : 'Expand table'}
            >
              {isExpanded ? (
                <ChevronDown className="h-5 w-5 text-gray-600" />
              ) : (
                <ChevronRight className="h-5 w-5 text-gray-600" />
              )}
            </button>
            <Users className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900">Net Staffing Analysis</h2>
          </div>

          {/* Summary Cards */}
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <div className="text-xs text-gray-500">Total Headcount Needed</div>
              <div className="text-lg font-bold text-gray-900">{summary.totalRequired}</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-500">Total Scheduled</div>
              <div className="text-lg font-bold text-gray-900">{summary.totalScheduled}</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-500">Net</div>
              <div className={`text-lg font-bold ${
                summary.totalNet < 0 ? 'text-red-600' : summary.totalNet > 0 ? 'text-green-600' : 'text-blue-600'
              }`}>
                {summary.totalNet > 0 ? '+' : ''}{summary.totalNet}
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-500">Avg SLA</div>
              <div className={`text-lg font-bold ${
                summary.avgSLA >= serviceLevelParams.slaPercent ? 'text-green-600' : 'text-red-600'
              }`}>
                {summary.avgSLA}%
              </div>
            </div>
          </div>
        </div>

        {/* Alert Banner */}
        {summary.gapCount > 0 && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-3 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium text-red-900">
                {summary.gapCount} interval{summary.gapCount !== 1 ? 's' : ''} understaffed
                (max gap: {Math.abs(summary.maxGap)} agents)
              </span>
            </div>
            <button
              onClick={() => onAutoFill?.('all', summary.maxGap)}
              className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 flex items-center space-x-1"
            >
              <Zap className="h-3 w-3" />
              <span>Auto-Fill All Gaps</span>
            </button>
          </div>
        )}
      </div>

      {/* Staffing Grid */}
      {isExpanded && (
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                  Time
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Volume
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-purple-700 uppercase tracking-wider bg-purple-50">
                  <div className="flex items-center justify-center space-x-1">
                    <span>Headcount Needed</span>
                    <div className="group relative">
                      <Info className="h-3 w-3 text-purple-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10 normal-case">
                        Calculated agents needed based on volume, AHT, and service level targets using Erlang C
                      </div>
                    </div>
                  </div>
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-blue-700 uppercase tracking-wider bg-blue-50">
                  Scheduled
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-100">
                  Net
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Expected SLA
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {staffingIntervals.map((interval) => {
                const netStyle = getCellStyle(interval.net, 'net');
                const slaStyle = getCellStyle(interval.sla, 'sla');
                const isHovered = hoveredInterval === interval.time;

                return (
                  <tr
                    key={interval.time}
                    className={`transition-colors ${isHovered ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
                    onMouseEnter={() => setHoveredInterval(interval.time)}
                    onMouseLeave={() => setHoveredInterval(null)}
                  >
                    {/* Time */}
                    <td className="sticky left-0 z-10 bg-white px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-200 whitespace-nowrap">
                      {interval.time}
                    </td>

                    {/* Volume */}
                    <td className="px-4 py-3 text-sm text-center text-gray-700">
                      {interval.callVolume}
                    </td>

                    {/* Required */}
                    <td className="px-4 py-3 text-sm text-center bg-purple-50">
                      <span className="font-semibold text-purple-900">{interval.required}</span>
                    </td>

                    {/* Scheduled */}
                    <td className="px-4 py-3 text-sm text-center bg-blue-50">
                      <span className="font-semibold text-blue-900">{interval.scheduled}</span>
                    </td>

                    {/* Net */}
                    <td className={`px-4 py-3 text-sm text-center ${netStyle.bg} border-2 ${netStyle.border}`}>
                      <div className="flex items-center justify-center space-x-1">
                        {netStyle.icon}
                        <span className={netStyle.text}>
                          {interval.net > 0 ? '+' : ''}{interval.net}
                        </span>
                      </div>
                    </td>

                    {/* Expected SLA */}
                    <td className={`px-4 py-3 text-sm text-center ${slaStyle.bg} border-2 ${slaStyle.border}`}>
                      <div className="flex items-center justify-center space-x-1">
                        {slaStyle.icon}
                        <span className={slaStyle.text}>{interval.sla}%</span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-sm text-center">
                      {interval.net < 0 && (
                        <button
                          onClick={() => onAutoFill?.(interval.time, interval.net)}
                          className="px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 transition-colors"
                          title={`Fill gap of ${Math.abs(interval.net)} agents`}
                        >
                          Fill Gap
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Footer Legend */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs text-gray-600">
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-50 border-2 border-green-200 rounded"></div>
            <span>Meeting or exceeding target</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-50 border-2 border-red-300 rounded"></div>
            <span>Below target (needs attention)</span>
          </div>
        </div>
        <div className="text-gray-500">
          💡 Click "Fill Gap" to see available agents who can cover understaffed intervals
        </div>
      </div>
    </div>
  );
}
